'use client';

import { useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { AlertTriangle, ArrowLeft, X } from 'lucide-react';
import { useChatBanner } from '@/components/chat-banner-provider';
import { useNotifications } from '@/hooks/useNotifications';
import { formatCredits } from '@/lib/balance/units';

interface Props {
  chatId: string;
}

// Tier A — sticky in-chat banner above the composer for critical
// (action-required) notifications when the user is actively viewing the
// matching chat. Today only payment_required is wired here; future
// settlement-failure / over-cap prompts will surface through the same
// channel via ChatBannerProvider.

/** Same money formatter as CriticalToast and the notification item: the
 *  amounts on a payment_required notification are credits, and
 *  `formatCredits` is the one formatter for a credit amount shown to a person
 *  (lib/balance/units.ts). This banner kept a local `$` formatter after the
 *  other two moved on 2026-09-07, so the same prompt read "$5" in chat and
 *  "5.00 credits" in the panel; unified 2026-09-22. */
const fmtMoney = formatCredits;

export function ChatPaymentBanner({ chatId }: Props) {
  const t = useT();
  const { banner, dismiss } = useChatBanner(chatId);
  const { performAction } = useNotifications(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Mirrors CriticalToast's picker state. See that file for the
  // rationale — the banner shares the same UX so users see one
  // pattern across surfaces.
  const [picker, setPicker] = useState<'actions' | 'quick' | 'custom'>(
    'actions',
  );
  const [customStr, setCustomStr] = useState('');
  const customInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (picker === 'custom') {
      requestAnimationFrame(() => customInputRef.current?.focus());
    }
  }, [picker]);

  if (!banner) return null;

  const { notification } = banner;
  if (notification.type !== 'payment_required') return null;
  if (notification.actionTaken) return null;

  const data = (notification.data as Record<string, unknown> | null) ?? {};
  const amount = data.amount as string | undefined;
  // Same base-amount rule as CriticalToast: prefer the SPEND LIMIT
  // that was hit (e.g. $5 daily cap → 2x = $10, 10x = $50) over the
  // trivial per-request amount. Falls back to amount when the
  // notification doesn't carry a limit (insufficient-balance,
  // tool-cost, generic agent request).
  const dailyLimitStr =
    typeof data.dailyLimit === 'string' ? (data.dailyLimit as string) : undefined;
  const amountNum = (() => {
    const n = typeof amount === 'string' ? parseFloat(amount) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 0;
  })();
  const dailyLimitNum = (() => {
    const n = typeof dailyLimitStr === 'string' ? parseFloat(dailyLimitStr) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 0;
  })();
  const pickerBase = dailyLimitNum > 0 ? dailyLimitNum : amountNum;
  const quick2x = pickerBase * 2;
  const quick10x = pickerBase * 10;

  async function take(
    action: 'approve_once' | 'always_approve' | 'deny',
    threshold?: number,
  ) {
    if (busy) return;
    setBusy(action);
    try {
      await performAction(notification.id, action, undefined, threshold);
    } finally {
      setBusy(null);
      dismiss();
    }
  }

  return (
    <div
      data-chat-payment-banner
      data-notification-id={notification.id}
      role="status"
      aria-live="polite"
      className="px-3 py-2.5 flex items-start gap-3 bg-amber-500/10 border-t border-amber-500/30 text-sm text-amber-900 dark:text-amber-200"
    >
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="flex-1 min-w-0">
        <div className="font-medium">{notification.title}</div>
        {notification.body && <div className="text-xs opacity-80 mt-0.5">{notification.body}</div>}
        <div className="mt-2 flex flex-wrap gap-2">
          {picker === 'actions' && (
            <>
              <button
                type="button"
                data-action="approve"
                disabled={busy !== null}
                onClick={() => take('approve_once')}
                className="px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50"
              >
                {busy === 'approve_once' ? 'Approving…' : `Approve${amountNum > 0 ? ` ${formatCredits(amountNum)}` : ''}`}
              </button>
              <button
                type="button"
                data-action="set_limit"
                disabled={busy !== null}
                onClick={() => setPicker('quick')}
                className="px-2.5 py-1 rounded-md bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs font-medium disabled:opacity-50"
              >
                {t('notifications.setLimit')}
              </button>
              <button
                type="button"
                data-action="deny"
                disabled={busy !== null}
                onClick={() => take('deny')}
                className="px-2.5 py-1 rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 text-xs font-medium disabled:opacity-50"
              >
                {busy === 'deny' ? t('criticalToast.denying') : t('notifications.deny')}
              </button>
            </>
          )}

          {picker === 'quick' && (
            // Inline back-arrow at the row start (no separate row /
            // trailing chip), then 2x / 10x / Custom. Matches the
            // CriticalToast layout 1:1.
            <>
              <button
                type="button"
                onClick={() => setPicker('actions')}
                disabled={busy !== null}
                aria-label={t('user.back')}
                className="inline-flex items-center justify-center h-6 w-6 rounded-md text-amber-700/80 dark:text-amber-300/80 hover:text-amber-900 dark:hover:text-amber-100 hover:bg-amber-500/10 disabled:opacity-50"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                data-action="set_limit_2x"
                disabled={busy !== null || pickerBase <= 0}
                onClick={() => take('always_approve', quick2x)}
                className="px-2.5 py-1 rounded-md bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs font-medium disabled:opacity-50"
                title={`Auto-approve up to ${fmtMoney(quick2x)} per request`}
              >
                2x · {fmtMoney(quick2x)}
              </button>
              <button
                type="button"
                data-action="set_limit_10x"
                disabled={busy !== null || pickerBase <= 0}
                onClick={() => take('always_approve', quick10x)}
                className="px-2.5 py-1 rounded-md bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs font-medium disabled:opacity-50"
                title={`Auto-approve up to ${fmtMoney(quick10x)} per request`}
              >
                10x · {fmtMoney(quick10x)}
              </button>
              <button
                type="button"
                data-action="set_limit_custom"
                disabled={busy !== null}
                onClick={() => setPicker('custom')}
                className="px-2.5 py-1 rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 text-xs font-medium disabled:opacity-50"
              >
                Custom…
              </button>
            </>
          )}

          {picker === 'custom' && (
            <form
              className="flex w-full items-center gap-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                const n = parseFloat(customStr);
                if (!Number.isFinite(n) || n <= 0) return;
                take('always_approve', n);
              }}
            >
              <button
                type="button"
                onClick={() => setPicker('quick')}
                disabled={busy !== null}
                aria-label={t('user.back')}
                className="inline-flex items-center justify-center h-6 w-6 rounded-md text-amber-700/80 dark:text-amber-300/80 hover:text-amber-900 dark:hover:text-amber-100 hover:bg-amber-500/10 disabled:opacity-50"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
              </button>
              <span className="text-[11px] opacity-80">Up to</span>
              <div className="inline-flex items-center rounded-md bg-zinc-100 dark:bg-zinc-800 px-2 text-zinc-800 dark:text-zinc-200">
                <input
                  ref={customInputRef}
                  type="number"
                  inputMode="decimal"
                  min={amountNum > 0 ? amountNum : 0.01}
                  step="0.01"
                  value={customStr}
                  onChange={(e) => setCustomStr(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setPicker('quick');
                    }
                  }}
                  placeholder={
                    pickerBase > 0
                      ? pickerBase.toFixed(2)
                      : amountNum > 0
                        ? amountNum.toFixed(2)
                        : '0.10'
                  }
                  disabled={busy !== null}
                  className="bg-transparent px-1 py-0.5 text-xs w-20 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-xs opacity-70">{t('settings.creditsUnit')}</span>
              </div>
              <button
                type="submit"
                data-action="set_limit_apply"
                disabled={
                  busy !== null ||
                  !Number.isFinite(parseFloat(customStr)) ||
                  parseFloat(customStr) <= 0
                }
                className="ml-auto px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50"
              >
                {busy === 'always_approve' ? t('criticalToast.setting') : t('content.apply')}
              </button>
            </form>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('chatBanner.dismissAria')}
        className="p-1 rounded-md text-amber-600/70 hover:text-amber-700 dark:text-amber-400/70 dark:hover:text-amber-300 hover:bg-amber-500/10 shrink-0"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
